import { useEffect, useMemo, useState } from "react";
import {
  ShieldAlert,
  ShieldCheck,
  AlertTriangle,
  Filter,
  CheckCircle2,
  AlertCircle,
  Tag,
  Code2,
  FileWarning,
  Copy,
  Info,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { fetchGtmCompliance } from "../api";
import type {
  GtmClientCompliance,
  GtmFinding,
  GtmSeverityLevel,
  GtmIssueType,
} from "../types";
import { LoadingState } from "../components/LoadingState";

const SEVERITY_CONFIG: Record<
  GtmSeverityLevel,
  { label: string; color: string; bg: string; border: string; icon: typeof AlertTriangle }
> = {
  CRITICAL: {
    label: "P0 Crítica (Consent)",
    color: "var(--red-400, #f87171)",
    bg: "rgba(239, 68, 68, 0.12)",
    border: "rgba(239, 68, 68, 0.35)",
    icon: ShieldAlert,
  },
  HIGH: {
    label: "P1 Alta (Duplicados / fuera de GTM)",
    color: "var(--orange-400, #fb923c)",
    bg: "rgba(249, 115, 22, 0.12)",
    border: "rgba(249, 115, 22, 0.35)",
    icon: AlertTriangle,
  },
  MEDIUM: {
    label: "P2 Media (Custom HTML)",
    color: "var(--yellow-400, #facc15)",
    bg: "rgba(234, 179, 8, 0.12)",
    border: "rgba(234, 179, 8, 0.35)",
    icon: Code2,
  },
  LOW: {
    label: "P3 Baja (Nomenclatura)",
    color: "var(--blue-400, #60a5fa)",
    bg: "rgba(59, 130, 246, 0.12)",
    border: "rgba(59, 130, 246, 0.35)",
    icon: Tag,
  },
};

const ISSUE_TYPE_CONFIG: Record<GtmIssueType, { label: string; description: string }> = {
  CONSENT_MISSING: {
    label: "Consent Mode Faltante",
    description: "Etiqueta activa de terceros o pauta sin consentimiento configurado (Ley 21.719 / GDPR).",
  },
  DUPLICATE_SUSPECT: {
    label: "Sospecha de Duplicado",
    description: "Mismo activador y tipo de etiqueta en eventos de conversión/marketing.",
  },
  OUTSIDE_CONTAINER: {
    label: "Tracker fuera del Contenedor",
    description: "Tracker que carga en el sitio en vivo y no está en el contenedor GTM (plugin o código del tema).",
  },
  HEAVY_LISTENER: {
    label: "Custom HTML Activo",
    description: "Script HTML personalizado en contenedor. Requiere revisión técnica.",
  },
  NAMING_VIOLATION: {
    label: "Nomenclatura no Estándar",
    description: "Nombre de etiqueta fuera del estándar Wolf (§10: [Plataforma] - [Tipo/Evento]).",
  },
};

const RULE_DESCRIPTIONS: Record<string, string> = {
  "SITE-CNS-001": "Cookies de analítica o marketing escritas en el sitio en vivo sin banner de consentimiento detectable.",
};

export function CompliancePage() {
  const [clients, setClients] = useState<GtmClientCompliance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filtros
  const [severityFilter, setSeverityFilter] = useState<string>("ALL");
  const [issueFilter, setIssueFilter] = useState<string>("ALL");
  const [expandedClients, setExpandedClients] = useState<Record<string, boolean>>({});

  useEffect(() => {
    fetchGtmCompliance()
      .then((res: any) => {
        if (res.clients && Array.isArray(res.clients)) {
          setClients(res.clients);
          // Expandir por defecto clientes con fallas críticas
          const initialExpanded: Record<string, boolean> = {};
          res.clients.forEach((c: GtmClientCompliance) => {
            if (c.counts.critical > 0 || c.counts.high > 0) {
              initialExpanded[c.client_slug] = true;
            }
          });
          setExpandedClients(initialExpanded);
        } else if (res.data && Array.isArray(res.data)) {
          // Adaptador para respuesta legacy o vista plana
          const mapped: GtmClientCompliance[] = [
            {
              client_slug: "wolfdigital.cl",
              container_id: "12088866",
              container_name: "wolfdigital.cl",
              workspace_name: "Default Workspace",
              total_tags: res.data.length,
              total_active_tags: res.data.length,
              health_score: 50,
              counts: { critical: 0, high: 0, medium: 0, low: 0 },
              findings: [],
              scanned_at: "",
            },
          ];
          setClients(mapped);
        }
        setLoading(false);
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });
  }, []);

  const toggleExpand = (slug: string) => {
    setExpandedClients((prev) => ({ ...prev, [slug]: !prev[slug] }));
  };

  // Totales agregados globales
  const totalStats = useMemo(() => {
    return clients.reduce(
      (acc, c) => {
        acc.critical += c.counts.critical;
        acc.high += c.counts.high;
        acc.medium += c.counts.medium;
        acc.low += c.counts.low;
        acc.totalContainers += 1;
        acc.avgHealth += c.health_score;
        return acc;
      },
      { critical: 0, high: 0, medium: 0, low: 0, totalContainers: 0, avgHealth: 0 }
    );
  }, [clients]);

  const avgScore = totalStats.totalContainers
    ? Math.round(totalStats.avgHealth / totalStats.totalContainers)
    : 100;

  if (loading) return <LoadingState />;
  if (error) {
    return (
      <div className="page">
        <section className="error-state">
          <ShieldAlert size={28} />
          <h1>Error cargando Compliance GTM</h1>
          <p>{error}</p>
        </section>
      </div>
    );
  }

  return (
    <div className="page page-compliance">
      {/* HEADER DE PÁGINA */}
      <section className="page-intro enter">
        <div>
          <span className="eyebrow orange">Auditoría Privacidad, Tracking y GTM</span>
          <h1>GTM Compliance & Consent Mode por Severidad</h1>
          <p>
            Monitoreo automatizado clasificado en 4 niveles de severidad: Consent Mode v2 (P0), 
            duplicados de conversión (P1), scripts Custom HTML (P2) y estándar de nomenclatura §10 (P3).
          </p>
        </div>
      </section>

      {/* MÉTRICAS GLOBALES RESUMEN */}
      <section
        className="enter"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "1rem",
          marginBottom: "1.5rem",
        }}
      >
        <div className="card" style={{ padding: "1.25rem" }}>
          <span style={{ fontSize: "0.82rem", color: "var(--text-dim, #94a3b8)" }}>Health Score Promedio</span>
          <div style={{ display: "flex", alignItems: "baseline", gap: "0.5rem", marginTop: "0.25rem" }}>
            <span
              style={{
                fontSize: "1.8rem",
                fontWeight: 700,
                color: avgScore >= 80 ? "var(--green-400, #4ade80)" : avgScore >= 50 ? "var(--yellow-400, #facc15)" : "var(--red-400, #f87171)",
              }}
            >
              {avgScore} / 100
            </span>
          </div>
          <span style={{ fontSize: "0.78rem", color: "var(--muted, #64748b)" }}>
            {totalStats.totalContainers} sitios / contenedores auditados
          </span>
        </div>

        <div className="card" style={{ padding: "1.25rem", borderLeft: "4px solid var(--red-500, #ef4444)" }}>
          <span style={{ fontSize: "0.82rem", color: "var(--text-dim, #94a3b8)" }}>P0 Críticas (Consent)</span>
          <div style={{ marginTop: "0.25rem", fontSize: "1.8rem", fontWeight: 700, color: "var(--red-400, #f87171)" }}>
            {totalStats.critical}
          </div>
          <span style={{ fontSize: "0.78rem", color: "var(--muted, #64748b)" }}>
            Riesgo legal Ley 21.719 / bloqueo pauta
          </span>
        </div>

        <div className="card" style={{ padding: "1.25rem", borderLeft: "4px solid var(--orange-500, #f97316)" }}>
          <span style={{ fontSize: "0.82rem", color: "var(--text-dim, #94a3b8)" }}>P1 Altas (Duplicados / fuera de GTM)</span>
          <div style={{ marginTop: "0.25rem", fontSize: "1.8rem", fontWeight: 700, color: "var(--orange-400, #fb923c)" }}>
            {totalStats.high}
          </div>
          <span style={{ fontSize: "0.78rem", color: "var(--muted, #64748b)" }}>
            Sospecha de doble conteo
          </span>
        </div>

        <div className="card" style={{ padding: "1.25rem", borderLeft: "4px solid var(--yellow-500, #eab308)" }}>
          <span style={{ fontSize: "0.82rem", color: "var(--text-dim, #94a3b8)" }}>P2 Medias (Custom HTML)</span>
          <div style={{ marginTop: "0.25rem", fontSize: "1.8rem", fontWeight: 700, color: "var(--yellow-400, #facc15)" }}>
            {totalStats.medium}
          </div>
          <span style={{ fontSize: "0.78rem", color: "var(--muted, #64748b)" }}>
            Scripts activos a revisar
          </span>
        </div>

        <div className="card" style={{ padding: "1.25rem", borderLeft: "4px solid var(--blue-500, #3b82f6)" }}>
          <span style={{ fontSize: "0.82rem", color: "var(--text-dim, #94a3b8)" }}>P3 Bajas (Nombres)</span>
          <div style={{ marginTop: "0.25rem", fontSize: "1.8rem", fontWeight: 700, color: "var(--blue-400, #60a5fa)" }}>
            {totalStats.low}
          </div>
          <span style={{ fontSize: "0.78rem", color: "var(--muted, #64748b)" }}>
            Desvíos estándar §10
          </span>
        </div>
      </section>

      {/* BARRA DE FILTROS INTERACTIVOS */}
      <section
        className="card enter"
        style={{
          padding: "1rem 1.25rem",
          marginBottom: "1.5rem",
          display: "flex",
          flexWrap: "wrap",
          gap: "1rem",
          alignItems: "center",
          justifyContent: "space-between",
          background: "var(--surface-sunken, #0f172a)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", color: "var(--text-dim, #94a3b8)" }}>
            <Filter size={15} />
            <span style={{ fontSize: "0.85rem", fontWeight: 600 }}>Severidad:</span>
          </div>
          {["ALL", "CRITICAL", "HIGH", "MEDIUM", "LOW"].map((sev) => {
            const isSel = severityFilter === sev;
            const label =
              sev === "ALL"
                ? "Todas"
                : sev === "CRITICAL"
                ? "P0 Crítica"
                : sev === "HIGH"
                ? "P1 Alta"
                : sev === "MEDIUM"
                ? "P2 Media"
                : "P3 Baja";
            return (
              <button
                key={sev}
                type="button"
                onClick={() => setSeverityFilter(sev)}
                style={{
                  padding: "0.3rem 0.75rem",
                  fontSize: "0.8rem",
                  borderRadius: "999px",
                  border: isSel ? "1px solid var(--accent, #6366f1)" : "1px solid rgba(255,255,255,0.08)",
                  background: isSel ? "var(--accent, #6366f1)" : "rgba(255,255,255,0.03)",
                  color: isSel ? "#fff" : "var(--text-dim, #94a3b8)",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                }}
              >
                {label}
              </button>
            );
          })}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
          <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--text-dim, #94a3b8)" }}>Tipo de Falla:</span>
          <select
            value={issueFilter}
            onChange={(e) => setIssueFilter(e.target.value)}
            style={{
              padding: "0.4rem 0.85rem",
              fontSize: "0.82rem",
              borderRadius: "8px",
              background: "var(--surface-2, #151923)",
              border: "1px solid rgba(255,255,255,0.15)",
              color: "var(--text-main, #f8fafc)",
              colorScheme: "dark",
              outline: "none",
              cursor: "pointer",
            }}
          >
            <option value="ALL" style={{ background: "#151923", color: "#f8fafc" }}>Todos los tipos de falla</option>
            <option value="CONSENT_MISSING" style={{ background: "#151923", color: "#f8fafc" }}>P0: Consent Mode Faltante (GTM-CNS-001)</option>
            <option value="DUPLICATE_SUSPECT" style={{ background: "#151923", color: "#f8fafc" }}>P1: Sospecha de Duplicado (GTM-TAG-001)</option>
            <option value="OUTSIDE_CONTAINER" style={{ background: "#151923", color: "#f8fafc" }}>P1: Tracker fuera del Contenedor (SITE-OUT-001)</option>
            <option value="HEAVY_LISTENER" style={{ background: "#151923", color: "#f8fafc" }}>P2: Custom HTML Activo (GTM-CLN-002)</option>
            <option value="NAMING_VIOLATION" style={{ background: "#151923", color: "#f8fafc" }}>P3: Nomenclatura no Estándar (GTM-CLN-003)</option>
          </select>
        </div>
      </section>

      {/* LISTADO DE CLIENTES Y HALLAZGOS */}
      <section className="enter" style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
        {clients.length === 0 ? (
          <div className="card" style={{ padding: "3rem", textAlign: "center", color: "var(--text-dim, #94a3b8)" }}>
            <ShieldCheck size={48} style={{ margin: "0 auto 1rem auto", color: "var(--green-500, #22c55e)" }} />
            <h3>No se registran contenedores GTM sincronizados</h3>
            <p style={{ marginTop: "0.5rem", fontSize: "0.9rem" }}>
              Asegúrate de que existan contenedores asociados en <code>sys_config.bridge_cliente_accounts</code>.
            </p>
          </div>
        ) : (
          clients.map((client) => {
            const isExpanded = !!expandedClients[client.client_slug];
            const hasCritical = client.counts.critical > 0;
            const hasHigh = client.counts.high > 0;

            // Filtrado de hallazgos del cliente
            const filteredFindings = client.findings.filter((f) => {
              if (severityFilter !== "ALL" && f.severity_level !== severityFilter) return false;
              if (issueFilter !== "ALL" && f.issue_type !== issueFilter) return false;
              return true;
            });

            return (
              <div
                key={client.client_slug}
                className="card"
                style={{
                  padding: "1.5rem",
                  borderTop: hasCritical
                    ? "4px solid var(--red-500, #ef4444)"
                    : hasHigh
                    ? "4px solid var(--orange-500, #f97316)"
                    : client.health_score >= 80
                    ? "4px solid var(--green-500, #22c55e)"
                    : "4px solid var(--yellow-500, #eab308)",
                }}
              >
                {/* CABECERA DEL CONTENEDOR */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                    gap: "1rem",
                    cursor: "pointer",
                  }}
                  onClick={() => toggleExpand(client.client_slug)}
                >
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
                      <h3 style={{ margin: 0, textTransform: "capitalize", fontSize: "1.2rem" }}>
                        {client.client_slug.replace(/_/g, " ")}
                      </h3>
                      {client.container_id === "SITIO_EN_VIVO" ? (
                        <span
                          style={{
                            fontSize: "0.75rem",
                            padding: "0.15rem 0.5rem",
                            borderRadius: "4px",
                            background: "rgba(59, 130, 246, 0.15)",
                            color: "var(--blue-400, #60a5fa)",
                            border: "1px solid rgba(59, 130, 246, 0.3)",
                            fontWeight: 600,
                          }}
                        >
                          Solo escaneo web (sin GTM)
                        </span>
                      ) : (
                        <span className="mono" style={{ fontSize: "0.8rem", color: "var(--muted, #64748b)" }}>
                          {client.container_name} ({client.container_id})
                        </span>
                      )}
                    </div>
                    <span style={{ fontSize: "0.85rem", color: "var(--text-dim, #94a3b8)", marginTop: "0.25rem", display: "inline-block" }}>
                      {client.container_id === "SITIO_EN_VIVO" ? (
                        <>Sitio en vivo auditado · URL: <span className="mono" style={{ color: "var(--text-bright, #f8fafc)" }}>{client.container_name}</span></>
                      ) : (
                        <>{client.total_tags} etiquetas ({client.total_active_tags} activas) · Workspace: {client.workspace_name || "Default"}</>
                      )}
                    </span>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
                    {/* HEALTH SCORE BADGE */}
                    <div style={{ textAlign: "right" }}>
                      <span style={{ fontSize: "0.75rem", color: "var(--text-dim, #94a3b8)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        Health Score
                      </span>
                      <div
                        style={{
                          fontSize: "1.3rem",
                          fontWeight: 700,
                          color:
                            client.health_score >= 80
                              ? "var(--green-400, #4ade80)"
                              : client.health_score >= 50
                              ? "var(--yellow-400, #facc15)"
                              : "var(--red-400, #f87171)",
                        }}
                      >
                        {client.health_score} / 100
                      </div>
                    </div>

                    {/* BOTÓN COLAPSABLE */}
                    <button
                      type="button"
                      style={{
                        background: "rgba(255,255,255,0.05)",
                        border: "1px solid rgba(255,255,255,0.1)",
                        borderRadius: "50%",
                        width: "32px",
                        height: "32px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "var(--text-dim, #94a3b8)",
                        cursor: "pointer",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpand(client.client_slug);
                      }}
                      aria-label="Expandir hallazgos"
                    >
                      {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </button>
                  </div>
                </div>

                {/* PILLS DE CONTEO POR SEVERIDAD */}
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "0.5rem",
                    marginTop: "1.25rem",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      padding: "0.2rem 0.6rem",
                      borderRadius: "6px",
                      fontSize: "0.78rem",
                      fontWeight: 600,
                      background: SEVERITY_CONFIG.CRITICAL.bg,
                      color: SEVERITY_CONFIG.CRITICAL.color,
                      border: `1px solid ${SEVERITY_CONFIG.CRITICAL.border}`,
                    }}
                  >
                    {client.counts.critical} Críticas P0
                  </span>
                  <span
                    style={{
                      padding: "0.2rem 0.6rem",
                      borderRadius: "6px",
                      fontSize: "0.78rem",
                      fontWeight: 600,
                      background: SEVERITY_CONFIG.HIGH.bg,
                      color: SEVERITY_CONFIG.HIGH.color,
                      border: `1px solid ${SEVERITY_CONFIG.HIGH.border}`,
                    }}
                  >
                    {client.counts.high} Altas P1
                  </span>
                  <span
                    style={{
                      padding: "0.2rem 0.6rem",
                      borderRadius: "6px",
                      fontSize: "0.78rem",
                      fontWeight: 600,
                      background: SEVERITY_CONFIG.MEDIUM.bg,
                      color: SEVERITY_CONFIG.MEDIUM.color,
                      border: `1px solid ${SEVERITY_CONFIG.MEDIUM.border}`,
                    }}
                  >
                    {client.counts.medium} Medias P2
                  </span>
                  <span
                    style={{
                      padding: "0.2rem 0.6rem",
                      borderRadius: "6px",
                      fontSize: "0.78rem",
                      fontWeight: 600,
                      background: SEVERITY_CONFIG.LOW.bg,
                      color: SEVERITY_CONFIG.LOW.color,
                      border: `1px solid ${SEVERITY_CONFIG.LOW.border}`,
                    }}
                  >
                    {client.counts.low} Bajas P3
                  </span>
                </div>

                {/* TABLA DE HALLAZGOS (EXPANDIBLE) */}
                {isExpanded && (
                  <div style={{ marginTop: "1.25rem", borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: "1.25rem" }}>
                    {filteredFindings.length === 0 ? (
                      <div style={{ padding: "1rem", textAlign: "center", color: "var(--text-dim, #94a3b8)", fontSize: "0.9rem" }}>
                        ✓ Ningún hallazgo coincide con los filtros seleccionados.
                      </div>
                    ) : (
                      <div className="table-scroll">
                        <table className="usage-table vault-table" style={{ width: "100%", fontSize: "0.85rem" }}>
                          <thead>
                            <tr>
                              <th style={{ width: "16%" }}>Severidad</th>
                              <th style={{ width: "32%" }}>Etiqueta (tag_name)</th>
                              <th style={{ width: "14%" }}>Tipo</th>
                              <th style={{ width: "38%" }}>Infracción & Diagnóstico</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredFindings.map((finding, idx) => {
                              const sevCfg = SEVERITY_CONFIG[finding.severity_level];
                              const issueCfg = ISSUE_TYPE_CONFIG[finding.issue_type];
                              const Icon = sevCfg.icon;

                              return (
                                <tr key={`${finding.tag_id}-${finding.issue_type}-${idx}`}>
                                  <td>
                                    <span
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "0.35rem",
                                        padding: "0.2rem 0.55rem",
                                        borderRadius: "4px",
                                        fontSize: "0.75rem",
                                        fontWeight: 600,
                                        background: sevCfg.bg,
                                        color: sevCfg.color,
                                        border: `1px solid ${sevCfg.border}`,
                                      }}
                                    >
                                      <Icon size={12} /> {finding.severity_level}
                                    </span>
                                  </td>
                                  <td>
                                    {/* CORRECCIÓN BUG ANTERIOR: SE MUESTRA EL tag_name REAL */}
                                    <strong style={{ color: "var(--text-main, #f8fafc)" }}>
                                      {finding.tag_name}
                                    </strong>
                                    <div style={{ fontSize: "0.75rem", color: "var(--muted, #64748b)" }} className="mono">
                                      ID: {finding.tag_id}
                                    </div>
                                  </td>
                                  <td>
                                    <span className="mono" style={{ fontSize: "0.8rem", color: "var(--text-dim, #94a3b8)" }}>
                                      {finding.tag_type}
                                    </span>
                                  </td>
                                  <td>
                                    <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}>
                                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                        <strong style={{ color: sevCfg.color, fontSize: "0.82rem" }}>
                                          [{finding.rule_id}] {issueCfg?.label || finding.issue_type}
                                        </strong>
                                      </div>
                                      <span style={{ fontSize: "0.8rem", color: "var(--text-dim, #cbd5e1)", lineHeight: 1.4 }}>
                                        {RULE_DESCRIPTIONS[finding.rule_id] ?? issueCfg?.description}
                                      </span>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </section>
    </div>
  );
}
